import type React from 'react';
import { CRYSTAL_CHARACTERS, type CrystalCharacter } from './guideCatalogue';

export interface Avatar {
  id: string;
  emoji: string;
  label: string;
  bg: string;
  isCrystal?: boolean;
  crystalData?: CrystalCharacter;
}

export const AVATARS: Avatar[] = [
  { id: 'youth-curls',   emoji: '👩🏾‍🦱', label: 'Curls',    bg: '#ec4899' },
  { id: 'youth-locs',    emoji: '🧑🏿‍🦱',  label: 'Locs',     bg: '#8b6fb0' },
  { id: 'youth-girl',    emoji: '👧🏾',   label: 'Bright',   bg: 'var(--color-warning)' },
  { id: 'youth-boy',     emoji: '👦🏿',   label: 'Bold',     bg: '#8b6fb0' },
  { id: 'youth-head',    emoji: '🧒🏾',   label: 'Light',    bg: '#0ea5e9' },
  { id: 'youth-crown',   emoji: '👩🏿‍🦲',  label: 'Crown',    bg: 'var(--color-accent)' },
  { id: 'youth-wrap',    emoji: '🧕🏾',   label: 'Grace',    bg: '#14b8a6' },
  { id: 'youth-king',    emoji: '👨🏾‍🦱', label: 'King',     bg: '#d97706' },
  { id: 'sunflower',     emoji: '🌻',    label: 'Sunflower',bg: 'var(--color-warning)' },
  { id: 'fire',          emoji: '🔥',    label: 'Warrior',  bg: 'var(--color-danger)' },
  { id: 'lotus',         emoji: '🪷',    label: 'Lotus',    bg: '#ec4899' },
  { id: 'star',          emoji: '⭐',    label: 'Star',     bg: 'var(--color-warning)' },
  { id: 'gem',           emoji: '💎',    label: 'Gem',      bg: '#06b6d4' },
  { id: 'heart',         emoji: '💜',    label: 'Heart',    bg: 'var(--color-accent)' },
  { id: 'spark',         emoji: '✨',    label: 'Spark',    bg: 'var(--color-accent)' },
  { id: 'moon',          emoji: '🌙',    label: 'Moon',     bg: '#7b5ca0' },
  ...CRYSTAL_CHARACTERS.map(c => ({
    id: c.id,
    emoji: '💎',
    label: c.label,
    bg: c.bg,
    isCrystal: true,
    crystalData: c,
  })),
];

export type AvatarSvgProps = {
  size?: number;
};

type AvatarSvgComponent = (props: AvatarSvgProps) => React.ReactElement;

export const AVATAR_SVG_MAP: Record<string, AvatarSvgComponent> = {
  'youth-curls': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-curls-head" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#e8a87c" />
          <stop offset="100%" stopColor="#c07a52" />
        </radialGradient>
        <radialGradient id="av-curls-hair" cx="50%" cy="25%" r="60%">
          <stop offset="0%" stopColor="#7d5a4f" />
          <stop offset="100%" stopColor="#4a3228" />
        </radialGradient>
      </defs>
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-curls-head)" />
      <path d="M32 52 Q28 24 60 20 Q92 24 88 52" fill="url(#av-curls-hair)" />
      <path d="M32 48 Q28 32 36 24 Q44 16 56 14" stroke="#4a3228" strokeWidth="5" strokeLinecap="round" fill="none" opacity="0.5" />
      <path d="M88 48 Q92 32 84 24 Q76 16 64 14" stroke="#4a3228" strokeWidth="5" strokeLinecap="round" fill="none" opacity="0.5" />
      <circle cx="32" cy="44" r="8" fill="url(#av-curls-hair)" />
      <circle cx="88" cy="44" r="8" fill="url(#av-curls-hair)" />
      <circle cx="26" cy="52" r="6" fill="url(#av-curls-hair)" />
      <circle cx="94" cy="52" r="6" fill="url(#av-curls-hair)" />
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#3d2a1a" />
      <circle cx="75" cy="67" r="4.5" fill="#3d2a1a" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 84 Q60 90 70 84" stroke="#c07a52" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="71" r="1.5" fill="#e8a87c" />
      <circle cx="74" cy="71" r="1.5" fill="#e8a87c" />
      <circle cx="46" cy="58" r="3.5" fill="#d4768a" opacity="0.45" />
      <circle cx="74" cy="58" r="3.5" fill="#d4768a" opacity="0.45" />
      <circle cx="40" cy="68" r="3" fill="#e8a87c" opacity="0.5" />
      <circle cx="80" cy="68" r="3" fill="#e8a87c" opacity="0.5" />
      <rect x="26" y="94" width="68" height="26" rx="14" fill="#ec4899" opacity="0.9" />
    </svg>
  ),

  'youth-locs': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-locs-head" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#9e6040" />
          <stop offset="100%" stopColor="#6b3c20" />
        </radialGradient>
        <radialGradient id="av-locs-hair" cx="50%" cy="25%" r="60%">
          <stop offset="0%" stopColor="#3d2a1a" />
          <stop offset="100%" stopColor="#1e1008" />
        </radialGradient>
      </defs>
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-locs-head)" />
      <path d="M32 52 Q28 22 60 18 Q92 22 88 52" fill="url(#av-locs-hair)" />
      {[28, 36, 44, 52, 60, 68, 76, 84, 92].map((x, i) => (
        <rect key={i} x={x - 3} y={i % 2 === 0 ? 10 : 14} width="6" height={i % 2 === 0 ? 40 : 36} rx="3" fill="#3d2a1a" opacity="0.85" />
      ))}
      <rect x="24" y="36" width="6" height="30" rx="3" fill="#3d2a1a" opacity="0.7" />
      <rect x="90" y="36" width="6" height="30" rx="3" fill="#3d2a1a" opacity="0.7" />
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#3d2a1a" />
      <circle cx="75" cy="67" r="4.5" fill="#3d2a1a" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 84 Q60 89 70 84" stroke="#6b3c20" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="58" r="3.5" fill="#d4768a" opacity="0.4" />
      <circle cx="74" cy="58" r="3.5" fill="#d4768a" opacity="0.4" />
      <rect x="26" y="94" width="68" height="26" rx="14" fill="#8b6fb0" opacity="0.9" />
    </svg>
  ),

  'youth-girl': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-girl-head" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#e8c49c" />
          <stop offset="100%" stopColor="#c49060" />
        </radialGradient>
        <radialGradient id="av-girl-hair" cx="50%" cy="25%" r="60%">
          <stop offset="0%" stopColor="#c4825a" />
          <stop offset="100%" stopColor="#8b4a28" />
        </radialGradient>
      </defs>
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-girl-head)" />
      <path d="M32 52 Q28 20 60 16 Q92 20 88 52" fill="url(#av-girl-hair)" />
      <path d="M28 52 Q22 70 26 88" stroke="url(#av-girl-hair)" strokeWidth="10" strokeLinecap="round" fill="none" />
      <path d="M92 52 Q98 70 94 88" stroke="url(#av-girl-hair)" strokeWidth="10" strokeLinecap="round" fill="none" />
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#4a2e10" />
      <circle cx="75" cy="67" r="4.5" fill="#4a2e10" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 84 Q60 91 70 84" stroke="#c49060" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="58" r="3.5" fill="#e87090" opacity="0.45" />
      <circle cx="74" cy="58" r="3.5" fill="#e87090" opacity="0.45" />
      <circle cx="40" cy="66" r="4" fill="#e8c49c" opacity="0.6" />
      <circle cx="80" cy="66" r="4" fill="#e8c49c" opacity="0.6" />
      <circle cx="60" cy="16" r="5" fill="var(--color-warning)" opacity="0.9" />
      <rect x="26" y="94" width="68" height="26" rx="14" fill="var(--color-warning)" opacity="0.9" />
    </svg>
  ),

  'youth-boy': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-boy-head" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#9e6040" />
          <stop offset="100%" stopColor="#6b3c20" />
        </radialGradient>
        <radialGradient id="av-boy-hair" cx="50%" cy="20%" r="60%">
          <stop offset="0%" stopColor="#2a1a10" />
          <stop offset="100%" stopColor="#100808" />
        </radialGradient>
      </defs>
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-boy-head)" />
      <path d="M32 52 Q30 28 60 22 Q90 28 88 52" fill="url(#av-boy-hair)" />
      <path d="M32 48 Q32 36 40 30" stroke="#2a1a10" strokeWidth="4" strokeLinecap="round" fill="none" opacity="0.6" />
      <path d="M88 48 Q88 36 80 30" stroke="#2a1a10" strokeWidth="4" strokeLinecap="round" fill="none" opacity="0.6" />
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#1a0a00" />
      <circle cx="75" cy="67" r="4.5" fill="#1a0a00" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 83 Q60 88 70 83" stroke="#6b3c20" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="58" r="3.5" fill="#d4768a" opacity="0.35" />
      <circle cx="74" cy="58" r="3.5" fill="#d4768a" opacity="0.35" />
      <rect x="26" y="94" width="68" height="26" rx="14" fill="#8b6fb0" opacity="0.9" />
    </svg>
  ),

  'youth-head': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-yhead-head" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#e8c49c" />
          <stop offset="100%" stopColor="#c49060" />
        </radialGradient>
        <radialGradient id="av-yhead-hair" cx="50%" cy="20%" r="60%">
          <stop offset="0%" stopColor="#7d5a4f" />
          <stop offset="100%" stopColor="#3a2218" />
        </radialGradient>
      </defs>
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-yhead-head)" />
      <path d="M32 54 Q30 26 60 20 Q90 26 88 54" fill="url(#av-yhead-hair)" />
      <path d="M32 46 Q30 36 36 28" stroke="#3a2218" strokeWidth="4" strokeLinecap="round" fill="none" opacity="0.5" />
      <path d="M88 46 Q90 36 84 28" stroke="#3a2218" strokeWidth="4" strokeLinecap="round" fill="none" opacity="0.5" />
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#3d2010" />
      <circle cx="75" cy="67" r="4.5" fill="#3d2010" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 83 Q60 89 70 83" stroke="#c49060" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="58" r="3.5" fill="#e87090" opacity="0.4" />
      <circle cx="74" cy="58" r="3.5" fill="#e87090" opacity="0.4" />
      <circle cx="40" cy="67" r="4" fill="#e8c49c" opacity="0.5" />
      <circle cx="80" cy="67" r="4" fill="#e8c49c" opacity="0.5" />
      <rect x="26" y="94" width="68" height="26" rx="14" fill="#0ea5e9" opacity="0.9" />
    </svg>
  ),

  'youth-crown': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-crown-head" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#9e6040" />
          <stop offset="100%" stopColor="#6b3c20" />
        </radialGradient>
      </defs>
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-crown-head)" />
      <path d="M32 48 Q32 34 60 30 Q88 34 88 48 Q80 46 76 52 Q68 44 60 48 Q52 44 44 52 Q40 46 32 48Z" fill="var(--color-warning)" />
      <circle cx="60" cy="28" r="5" fill="var(--color-warning)" />
      <circle cx="38" cy="42" r="4" fill="var(--color-warning)" />
      <circle cx="82" cy="42" r="4" fill="var(--color-warning)" />
      <circle cx="60" cy="28" r="2.5" fill="var(--color-danger)" />
      <circle cx="38" cy="42" r="2" fill="var(--color-danger)" />
      <circle cx="82" cy="42" r="2" fill="var(--color-danger)" />
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#1a0a00" />
      <circle cx="75" cy="67" r="4.5" fill="#1a0a00" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 83 Q60 88 70 83" stroke="#6b3c20" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="58" r="3.5" fill="#d4768a" opacity="0.4" />
      <circle cx="74" cy="58" r="3.5" fill="#d4768a" opacity="0.4" />
      <rect x="26" y="94" width="68" height="26" rx="14" fill="var(--color-accent)" opacity="0.9" />
    </svg>
  ),

  'youth-wrap': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-wrap-head" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#c4825a" />
          <stop offset="100%" stopColor="#9e5030" />
        </radialGradient>
        <radialGradient id="av-wrap-cloth" cx="50%" cy="30%" r="60%">
          <stop offset="0%" stopColor="#14b8a6" />
          <stop offset="100%" stopColor="#0d8a7a" />
        </radialGradient>
      </defs>
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-wrap-head)" />
      <path d="M30 54 Q28 26 60 22 Q92 26 90 54 Q82 44 60 46 Q38 44 30 54Z" fill="url(#av-wrap-cloth)" />
      <path d="M90 54 Q96 46 94 36 Q92 28 88 24" stroke="#0d8a7a" strokeWidth="4" strokeLinecap="round" fill="none" />
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#3d1a10" />
      <circle cx="75" cy="67" r="4.5" fill="#3d1a10" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 84 Q60 90 70 84" stroke="#9e5030" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="58" r="3.5" fill="#d4768a" opacity="0.4" />
      <circle cx="74" cy="58" r="3.5" fill="#d4768a" opacity="0.4" />
      <circle cx="40" cy="67" r="4" fill="#c4825a" opacity="0.5" />
      <circle cx="80" cy="67" r="4" fill="#c4825a" opacity="0.5" />
      <rect x="26" y="94" width="68" height="26" rx="14" fill="#14b8a6" opacity="0.9" />
    </svg>
  ),

  'youth-king': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-king-head" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#c4825a" />
          <stop offset="100%" stopColor="#9e5030" />
        </radialGradient>
        <radialGradient id="av-king-hair" cx="50%" cy="20%" r="60%">
          <stop offset="0%" stopColor="#7d5a4f" />
          <stop offset="100%" stopColor="#4a3228" />
        </radialGradient>
      </defs>
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-king-head)" />
      <path d="M32 52 Q30 26 60 20 Q90 26 88 52" fill="url(#av-king-hair)" />
      <path d="M32 48 Q30 36 36 28" stroke="#4a3228" strokeWidth="4" strokeLinecap="round" fill="none" opacity="0.55" />
      <path d="M88 48 Q90 36 84 28" stroke="#4a3228" strokeWidth="4" strokeLinecap="round" fill="none" opacity="0.55" />
      <path d="M42 28 Q50 22 60 20 Q70 22 78 28" stroke="#d97706" strokeWidth="4" fill="none" strokeLinecap="round" />
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#2a1a00" />
      <circle cx="75" cy="67" r="4.5" fill="#2a1a00" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 84 Q60 90 70 84" stroke="#9e5030" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <path d="M48 80 Q52 78 50 74" stroke="#9e5030" strokeWidth="1.5" strokeLinecap="round" fill="none" />
      <path d="M72 80 Q68 78 70 74" stroke="#9e5030" strokeWidth="1.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="58" r="3.5" fill="#d4768a" opacity="0.4" />
      <circle cx="74" cy="58" r="3.5" fill="#d4768a" opacity="0.4" />
      <rect x="26" y="94" width="68" height="26" rx="14" fill="#d97706" opacity="0.9" />
    </svg>
  ),

  'sunflower': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-sun-face" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#e8c49c" />
          <stop offset="100%" stopColor="#c49060" />
        </radialGradient>
      </defs>
      {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
        <ellipse
          key={i}
          cx={60 + 28 * Math.cos((angle * Math.PI) / 180)}
          cy={60 + 28 * Math.sin((angle * Math.PI) / 180)}
          rx="8" ry="12"
          transform={`rotate(${angle + 90} ${60 + 28 * Math.cos((angle * Math.PI) / 180)} ${60 + 28 * Math.sin((angle * Math.PI) / 180)})`}
          fill="var(--color-warning)"
          opacity="0.9"
        />
      ))}
      <circle cx="60" cy="60" r="28" fill="url(#av-sun-face)" />
      <circle cx="60" cy="60" r="18" fill="#92400e" opacity="0.15" />
      <ellipse cx="49" cy="55" rx="6" ry="7" fill="white" opacity="0.95" />
      <ellipse cx="71" cy="55" rx="6" ry="7" fill="white" opacity="0.95" />
      <circle cx="50" cy="56" r="4" fill="#3d2010" />
      <circle cx="72" cy="56" r="4" fill="#3d2010" />
      <circle cx="51.5" cy="54.5" r="1.4" fill="white" />
      <circle cx="73.5" cy="54.5" r="1.4" fill="white" />
      <path d="M52 73 Q60 79 68 73" stroke="#c49060" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="49" cy="50" r="3" fill="#e87090" opacity="0.4" />
      <circle cx="71" cy="50" r="3" fill="#e87090" opacity="0.4" />
    </svg>
  ),

  'fire': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-fire-face" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#c4825a" />
          <stop offset="100%" stopColor="#9e5030" />
        </radialGradient>
        <radialGradient id="av-fire-hair" cx="50%" cy="25%" r="60%">
          <stop offset="0%" stopColor="var(--color-danger)" />
          <stop offset="100%" stopColor="#b91c1c" />
        </radialGradient>
      </defs>
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-fire-face)" />
      <path d="M32 52 Q30 24 60 18 Q90 24 88 52" fill="url(#av-fire-hair)" />
      <path d="M44 22 Q46 10 52 16 Q54 6 60 14 Q66 6 68 16 Q74 10 76 22" stroke="#f97316" strokeWidth="4" fill="none" strokeLinecap="round" />
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#2a0a00" />
      <circle cx="75" cy="67" r="4.5" fill="#2a0a00" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 84 Q60 89 70 84" stroke="#9e5030" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="58" r="3.5" fill="#f97316" opacity="0.45" />
      <circle cx="74" cy="58" r="3.5" fill="#f97316" opacity="0.45" />
    </svg>
  ),

  'lotus': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-lotus-face" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#e8c49c" />
          <stop offset="100%" stopColor="#c08060" />
        </radialGradient>
      </defs>
      <path d="M60 18 Q50 10 42 18 Q34 26 38 36 Q44 28 60 28Z" fill="#ec4899" opacity="0.9" />
      <path d="M60 18 Q70 10 78 18 Q86 26 82 36 Q76 28 60 28Z" fill="#ec4899" opacity="0.9" />
      <path d="M42 36 Q30 28 26 40 Q22 52 32 54 Q36 44 44 42Z" fill="#f472b6" opacity="0.8" />
      <path d="M78 36 Q90 28 94 40 Q98 52 88 54 Q84 44 76 42Z" fill="#f472b6" opacity="0.8" />
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-lotus-face)" />
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#3d1020" />
      <circle cx="75" cy="67" r="4.5" fill="#3d1020" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 84 Q60 90 70 84" stroke="#c08060" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="58" r="3.5" fill="#f472b6" opacity="0.4" />
      <circle cx="74" cy="58" r="3.5" fill="#f472b6" opacity="0.4" />
    </svg>
  ),

  'star': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-star-face" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#e8a87c" />
          <stop offset="100%" stopColor="#c07a52" />
        </radialGradient>
      </defs>
      {[0, 60, 120, 180, 240, 300].map((angle, i) => (
        <polygon
          key={i}
          points={`${60 + 32 * Math.cos((angle * Math.PI) / 180)},${60 + 32 * Math.sin((angle * Math.PI) / 180)} ${60 + 20 * Math.cos(((angle + 30) * Math.PI) / 180)},${60 + 20 * Math.sin(((angle + 30) * Math.PI) / 180)} ${60 + 32 * Math.cos(((angle + 60) * Math.PI) / 180)},${60 + 32 * Math.sin(((angle + 60) * Math.PI) / 180)}`}
          fill="var(--color-warning)"
          opacity="0.85"
        />
      ))}
      <circle cx="60" cy="60" r="28" fill="url(#av-star-face)" />
      <ellipse cx="46" cy="57" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="57" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="58" r="4.5" fill="#2a1a10" />
      <circle cx="75" cy="58" r="4.5" fill="#2a1a10" />
      <circle cx="48.5" cy="56" r="1.5" fill="white" />
      <circle cx="76.5" cy="56" r="1.5" fill="white" />
      <path d="M50 75 Q60 81 70 75" stroke="#c07a52" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="50" r="3.5" fill="var(--color-warning)" opacity="0.4" />
      <circle cx="74" cy="50" r="3.5" fill="var(--color-warning)" opacity="0.4" />
    </svg>
  ),

  'gem': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-gem-face" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#c4825a" />
          <stop offset="100%" stopColor="#9e5030" />
        </radialGradient>
        <radialGradient id="av-gem-hair" cx="50%" cy="20%" r="60%">
          <stop offset="0%" stopColor="#2a1a10" />
          <stop offset="100%" stopColor="#100808" />
        </radialGradient>
      </defs>
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-gem-face)" />
      <path d="M32 54 Q30 26 60 22 Q90 26 88 54" fill="url(#av-gem-hair)" />
      <polygon points="60,10 72,22 60,28 48,22" fill="#06b6d4" opacity="0.9" />
      <polygon points="48,22 60,28 52,18" fill="#0ea5e9" opacity="0.7" />
      <polygon points="72,22 60,28 68,18" fill="#0ea5e9" opacity="0.7" />
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#0a2030" />
      <circle cx="75" cy="67" r="4.5" fill="#0a2030" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 84 Q60 89 70 84" stroke="#9e5030" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="58" r="3.5" fill="#06b6d4" opacity="0.4" />
      <circle cx="74" cy="58" r="3.5" fill="#06b6d4" opacity="0.4" />
    </svg>
  ),

  'heart': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-heart-face" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#e8a87c" />
          <stop offset="100%" stopColor="#c07a52" />
        </radialGradient>
        <radialGradient id="av-heart-hair" cx="50%" cy="20%" r="60%">
          <stop offset="0%" stopColor="#7d5a4f" />
          <stop offset="100%" stopColor="#4a3228" />
        </radialGradient>
      </defs>
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-heart-face)" />
      <path d="M32 52 Q30 24 60 18 Q90 24 88 52" fill="url(#av-heart-hair)" />
      <path d="M50 18 Q54 10 60 14 Q66 10 70 18 Q68 10 60 8 Q52 10 50 18Z" fill="var(--color-accent)" opacity="0.9" />
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#2a1020" />
      <circle cx="75" cy="67" r="4.5" fill="#2a1020" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 84 Q60 91 70 84" stroke="#c07a52" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="58" r="3.5" fill="var(--color-accent)" opacity="0.4" />
      <circle cx="74" cy="58" r="3.5" fill="var(--color-accent)" opacity="0.4" />
      <path d="M53 22 Q56 17 60 20 Q64 17 67 22 Q64 25 60 28 Q56 25 53 22Z" fill="var(--color-accent)" opacity="0.85" />
    </svg>
  ),

  'spark': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-spark-face" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#c4825a" />
          <stop offset="100%" stopColor="#9e5030" />
        </radialGradient>
        <radialGradient id="av-spark-hair" cx="50%" cy="20%" r="60%">
          <stop offset="0%" stopColor="var(--color-accent)" />
          <stop offset="100%" stopColor="var(--color-accent-hover)" />
        </radialGradient>
      </defs>
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-spark-face)" />
      <path d="M32 52 Q30 24 60 20 Q90 24 88 52" fill="url(#av-spark-hair)" />
      {[30, 60, 90, 120, 150, 180].map((angle, i) => (
        <line key={i}
          x1={60} y1={25}
          x2={60 + 12 * Math.cos((angle * Math.PI) / 180)}
          y2={25 + 12 * Math.sin((angle * Math.PI) / 180)}
          stroke="#a78bfa" strokeWidth="2.5" strokeLinecap="round" opacity="0.8"
        />
      ))}
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#1a0a30" />
      <circle cx="75" cy="67" r="4.5" fill="#1a0a30" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 84 Q60 90 70 84" stroke="#9e5030" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="58" r="3.5" fill="#a78bfa" opacity="0.4" />
      <circle cx="74" cy="58" r="3.5" fill="#a78bfa" opacity="0.4" />
    </svg>
  ),

  'moon': ({ size = 60 }) => (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true" fill="none">
      <defs>
        <radialGradient id="av-moon-face" cx="45%" cy="40%" r="55%">
          <stop offset="0%" stopColor="#9e6040" />
          <stop offset="100%" stopColor="#6b3c20" />
        </radialGradient>
        <radialGradient id="av-moon-hair" cx="50%" cy="20%" r="60%">
          <stop offset="0%" stopColor="#7b5ca0" />
          <stop offset="100%" stopColor="#2e1d8e" />
        </radialGradient>
      </defs>
      <ellipse cx="60" cy="68" rx="28" ry="32" fill="url(#av-moon-face)" />
      <path d="M32 52 Q30 24 60 20 Q90 24 88 52" fill="url(#av-moon-hair)" />
      <path d="M54 12 Q44 16 40 24 Q36 34 42 42 Q50 32 60 32 Q64 20 54 12Z" fill="#a78bfa" opacity="0.9" />
      <circle cx="38" cy="16" r="3" fill="white" opacity="0.7" />
      <circle cx="30" cy="28" r="2" fill="white" opacity="0.5" />
      <circle cx="44" cy="10" r="1.5" fill="white" opacity="0.6" />
      <ellipse cx="46" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <ellipse cx="74" cy="66" rx="7" ry="8" fill="white" opacity="0.95" />
      <circle cx="47" cy="67" r="4.5" fill="#0a1040" />
      <circle cx="75" cy="67" r="4.5" fill="#0a1040" />
      <circle cx="48.5" cy="65" r="1.5" fill="white" />
      <circle cx="76.5" cy="65" r="1.5" fill="white" />
      <path d="M50 84 Q60 89 70 84" stroke="#6b3c20" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <circle cx="46" cy="58" r="3.5" fill="#a78bfa" opacity="0.4" />
      <circle cx="74" cy="58" r="3.5" fill="#a78bfa" opacity="0.4" />
    </svg>
  ),
};
